import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trees,
  LayoutDashboard,
  CalendarDays,
  Timer,
  Users,
  Trophy,
  Brain,
  LogOut,
  Menu,
  X,
  Zap,
  Flame,
  Newspaper,
  BookOpen,
  Search,
  NotebookPen,
  ClipboardCheck,
  Mic,
  Shield,
  Sparkles,
  Sun,
  Moon,
} from 'lucide-react';
import { useAuthStore, useTimerStore } from '../lib/store';
import { cn } from '../lib/utils';
import { NotificationSystem } from './NotificationSystem';
import { useRealtimeSync } from '../hooks/useRealtimeSync';
import ParallaxBackground from './ParallaxBackground';
import PulsingFAB from './PulsingFAB';
import GuidedTour from './GuidedTour';

const baseNavItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, tourId: 'tour-nav-dashboard' },
  { to: '/tasks', label: 'Planner', icon: CalendarDays, tourId: 'tour-nav-tasks' },
  { to: '/timer', label: 'Focus Timer', icon: Timer, tourId: 'tour-nav-timer' },
  { to: '/rooms', label: 'Study Rooms', icon: Users, tourId: undefined },
  { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, tourId: undefined },
  { to: '/feed', label: 'Community', icon: Newspaper, tourId: undefined },
  { to: '/blog', label: 'Blog Studio', icon: NotebookPen, tourId: undefined },
  { to: '/subjects', label: 'Subjects', icon: BookOpen, tourId: undefined },
  { to: '/notes', label: 'Study Vault', icon: NotebookPen, tourId: undefined },
  { to: '/search', label: 'Search', icon: Search, tourId: undefined },
  { to: '/review', label: 'Weekly Review', icon: ClipboardCheck, tourId: undefined },
  { to: '/forest-history', label: 'Forest History', icon: Trees, tourId: undefined },
  { to: '/evolution', label: 'Ecosystem', icon: Sparkles, tourId: undefined },
  { to: '/achievements', label: 'Achievements', icon: Trophy, tourId: undefined },
  { to: '/voice', label: 'Voice Hub', icon: Mic, tourId: undefined },
  { to: '/coach', label: 'AI Coach', icon: Brain, tourId: undefined },
];

const mobilePrimaryNav = ['/dashboard', '/tasks', '/timer', '/subjects', '/notes'];

const AppLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuthStore();
  const { isRunning, elapsedSeconds } = useTimerStore();
  const [immersiveMode, setImmersiveMode] = useState(false);
  
  // Light Mode State
  const [isLightMode, setIsLightMode] = useState(() => {
    return localStorage.getItem('focusflow_theme') === 'light';
  });

  // Apply theme to document element
  useEffect(() => {
    if (isLightMode) {
      document.documentElement.setAttribute('data-theme', 'light');
      localStorage.setItem('focusflow_theme', 'light');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('focusflow_theme', 'dark');
    }
  }, [isLightMode]);

  useRealtimeSync();

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{ active?: boolean }>;
      setImmersiveMode(Boolean(custom.detail?.active));
    };
    window.addEventListener('focusflow:immersive-mode', handler as EventListener);
    return () => window.removeEventListener('focusflow:immersive-mode', handler as EventListener);
  }, []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const hideNavigation = immersiveMode && location.pathname === '/timer';

  const navItems =
    profile?.role && ['admin', 'super_admin', 'moderator'].includes(profile.role)
      ? [...baseNavItems, { to: '/admin', label: 'Admin Panel', icon: Shield, tourId: undefined }]
      : baseNavItems;

  const mobileNavItems = navItems.filter((item) => mobilePrimaryNav.includes(item.to));

  const formatTimer = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen bg-[#0B0F1A] text-white" style={{ position: 'relative' }}>
      <ParallaxBackground />
      <NotificationSystem />

      <aside
        className={cn(
          'glass-panel fixed z-40 hidden h-full w-64 flex-col border-r border-white/[0.06] lg:flex',
          hideNavigation && 'lg:hidden'
        )}
        style={{ borderRight: '1px solid rgba(255,255,255,0.06)' }}
      >
        <div className="p-6">
          <motion.div
            id="tour-logo"
            className="flex cursor-pointer items-center gap-2"
            onClick={() => navigate('/dashboard')}
            whileHover={{ scale: 1.04 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <div
              className="flex h-9 w-9 items-center justify-center rounded-xl"
              style={{
                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                boxShadow: '0 0 18px rgba(124,58,237,0.45), inset 0 1px 0 rgba(255,255,255,0.15)',
              }}
            >
              <Trees className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight">FocusFlow</span>
          </motion.div>
        </div>

        <AnimatePresence>
          {isRunning && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mx-4 mb-4"
            >
              <div
                className="flex items-center gap-3 rounded-xl px-4 py-3"
                style={{
                  background: 'rgba(124,58,237,0.10)',
                  border: '1px solid rgba(124,58,237,0.28)',
                  boxShadow: 'inset 0 0 16px rgba(124,58,237,0.06)',
                }}
              >
                <div className="relative">
                  <div className="h-2 w-2 rounded-full bg-green-400" />
                  <div className="absolute inset-0 h-2 w-2 animate-ping rounded-full bg-green-400" />
                </div>
                <div>
                  <p className="text-xs text-[#94A3B8]">Focusing</p>
                  <p className="text-lg font-mono font-bold text-[#00E5FF]">{formatTimer(elapsedSeconds)}</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <nav className="flex-1 space-y-0.5 px-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              id={item.tourId}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all',
                  isActive ? 'nav-active text-white' : 'text-[#94A3B8] hover:bg-white/[0.04] hover:text-white'
                )
              }
            >
              {({ isActive }) => (
                <motion.div
                  className="flex w-full items-center gap-3"
                  whileHover={{ x: isActive ? 0 : 4 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                >
                  <item.icon
                    className="h-5 w-5 flex-shrink-0"
                    style={isActive ? { filter: 'drop-shadow(0 0 6px rgba(124,58,237,0.6))' } : {}}
                  />
                  {item.label}
                </motion.div>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/[0.05] p-4">
          {profile && (
            <div
              className="rounded-xl p-3"
              style={{
                background: 'rgba(255,255,255,0.025)',
                border: '1px solid rgba(255,255,255,0.06)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
              }}
            >
              <div className="mb-3 flex items-center gap-3">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED, #00E5FF)',
                    boxShadow: '0 0 12px rgba(124,58,237,0.45)',
                  }}
                >
                  {profile.username.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{profile.username}</p>
                  <p className="text-xs text-[#94A3B8]">Level {profile.level}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1 text-[#00E5FF]">
                  <Zap className="h-3 w-3" /> {profile.xp} XP
                </span>
                <span className="flex items-center gap-1 text-orange-400">
                  <Flame className="h-3 w-3" /> {profile.streak} streak
                </span>
              </div>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setIsLightMode(!isLightMode)}
              className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm text-[#94A3B8] transition-colors hover:bg-white/5 hover:text-white"
              aria-label="Toggle Light Mode"
            >
              {isLightMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />} 
              <span className="hidden lg:inline">{isLightMode ? 'Dark' : 'Light'}</span>
            </button>
            <button
              onClick={handleSignOut}
              className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm text-[#94A3B8] transition-colors hover:bg-red-500/10 hover:text-red-400"
              aria-label="Sign Out"
            >
              <LogOut className="h-4 w-4" /> 
              <span className="hidden lg:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      <div
        className={cn(
          'fixed top-0 z-50 flex w-full items-center justify-between px-4 py-3 lg:hidden',
          hideNavigation && 'hidden'
        )}
        style={{
          background: 'rgba(11,15,26,0.88)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <div className="flex items-center gap-2">
          <Trees className="h-5 w-5 text-[#7C3AED]" />
          <span className="text-lg font-bold">FocusFlow</span>
        </div>
        <div className="flex items-center gap-2">
          {isRunning && <span className="text-sm font-mono text-[#00E5FF] mr-1">{formatTimer(elapsedSeconds)}</span>}
          <button onClick={() => setIsLightMode(!isLightMode)} className="rounded-lg p-2 text-[#94A3B8] hover:bg-white/5 hover:text-white">
            {isLightMode ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </button>
          <button onClick={() => setSidebarOpen((current) => !current)} className="rounded-lg p-2 hover:bg-white/5">
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/60 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="glass-panel fixed bottom-0 left-0 top-0 z-50 w-72 p-4 pt-16 lg:hidden"
            >
              <nav className="max-h-[calc(100vh-140px)] space-y-1 overflow-y-auto pr-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all',
                        isActive ? 'nav-active text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'
                      )
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </NavLink>
                ))}
              </nav>
              <button
                onClick={handleSignOut}
                className="mt-6 flex w-full items-center gap-2 rounded-xl px-4 py-3 text-sm text-[#94A3B8] transition-colors hover:text-red-400"
              >
                <LogOut className="h-4 w-4" /> Sign Out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main
        className={cn('min-h-screen flex-1', hideNavigation ? 'pt-0 lg:ml-0' : 'pt-16 lg:ml-64 lg:pt-0')}
        style={{ position: 'relative', zIndex: 1 }}
      >
        <div className={cn('mx-auto max-w-7xl', hideNavigation ? 'p-0' : 'px-3 pb-24 pt-4 sm:px-4 lg:px-8 lg:py-8')}>
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {!hideNavigation && (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-[rgba(11,15,26,0.92)] px-2 py-2 backdrop-blur-xl lg:hidden">
          <div className="mx-auto flex max-w-md items-center justify-around gap-1">
            {mobileNavItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  className={cn(
                    'flex min-w-0 flex-1 flex-col items-center gap-1 rounded-2xl px-2 py-2 text-[11px] font-medium transition',
                    active ? 'bg-[#8B5CF6]/18 text-white' : 'text-[#94A3B8]'
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  <span className="truncate">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div id="tour-fab">
        <PulsingFAB />
      </div>
      <GuidedTour />
    </div>
  );
};

export default AppLayout;
